/**
 * Phase 7B — Labs Mini Panel
 *
 * Quick-action overlay for lab/screening orders. Shows AI-captured labs,
 * allows quick confirm/dismiss. Add-more dropdown for common lab sets.
 * Full ordering engine lives on the Finalization Page.
 */
import React, { useState } from 'react';
import { api } from '../../services/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  X, Check, TestTubes, ArrowRight, Loader2, ChevronDown, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const COMMON_LAB_SETS = [
  { name: 'CBC', tests: ['CBC with Differential'] },
  { name: 'BMP', tests: ['Basic Metabolic Panel'] },
  { name: 'CMP', tests: ['Comprehensive Metabolic Panel'] },
  { name: 'Lipid Panel', tests: ['Total Cholesterol', 'LDL', 'HDL', 'Triglycerides'] },
  { name: 'HbA1c', tests: ['Hemoglobin A1c'] },
  { name: 'TSH', tests: ['Thyroid Stimulating Hormone'] },
  { name: 'UA', tests: ['Urinalysis'] },
];

export default function MiniPanelLabs({
  enc, outputs, encounterId, fetchEncounter,
  onClose, onOpenFull,
}) {
  const [showAddMore, setShowAddMore] = useState(false);

  const labActions = (enc?.suggested_actions || []).filter(a =>
    a.action_type === 'screening' || a.action_type === 'lab_order' ||
    a.action_name?.toLowerCase().includes('lab') || a.action_name?.toLowerCase().includes('screen')
  );

  return (
    <MiniPanelShell onClose={onClose} onOpenFull={onOpenFull} title="Labs" count={labActions.length}>
      {labActions.length === 0 ? (
        <div className="text-center py-3">
          <TestTubes className="w-5 h-5 mx-auto mb-1.5 text-muted-foreground/30" />
          <p className="text-[10px] text-muted-foreground">No labs captured from note</p>
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="mini-panel-labs-list">
          {labActions.map((action, i) => (
            <LabQuickRow
              key={action.id}
              action={action}
              index={i}
              encounterId={encounterId}
              fetchEncounter={fetchEncounter}
            />
          ))}
        </div>
      )}

      {/* Add More dropdown */}
      <div className="mt-2 pt-2 border-t border-border/50">
        <button
          onClick={() => setShowAddMore(!showAddMore)}
          className="flex items-center gap-1 text-[10px] text-primary hover:text-primary/80 font-medium w-full"
          data-testid="labs-add-more-toggle"
        >
          <Plus className="w-3 h-3" />
          Add Lab Set
          <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${showAddMore ? 'rotate-180' : ''}`} />
        </button>
        {showAddMore && (
          <div className="mt-1.5 space-y-0.5 animate-in fade-in-0 slide-in-from-top-1 duration-100"
            data-testid="labs-add-more-list">
            {COMMON_LAB_SETS.map(set => (
              <button
                key={set.name}
                className="w-full text-left px-2 py-1.5 rounded text-[10px] hover:bg-accent transition-colors flex items-center gap-1.5"
                onClick={async () => {
                  try {
                    await api.post(`/encounters/${encounterId}/suggested-actions/${encounterId}/execute`, {
                      action: 'add_lab_set',
                      lab_set_name: set.name,
                    });
                    toast.success(`${set.name} added to staging`);
                    fetchEncounter();
                    setShowAddMore(false);
                  } catch {
                    // Lab set addition is aspirational — the API may not support it yet.
                    // For now, just show it was staged
                    toast.info(`${set.name} — will be available in Finalization`);
                    setShowAddMore(false);
                  }
                }}
                data-testid={`add-lab-set-${set.name}`}
              >
                <TestTubes className="w-3 h-3 text-muted-foreground" />
                <span className="font-medium">{set.name}</span>
                <span className="text-muted-foreground ml-auto">{set.tests.length} test{set.tests.length > 1 ? 's' : ''}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </MiniPanelShell>
  );
}


function LabQuickRow({ action, index, encounterId, fetchEncounter }) {
  const [loading, setLoading] = useState(false);
  const isPending = action.status === 'pending';

  const handleAction = async (act) => {
    setLoading(true);
    try {
      await api.post(`/encounters/${encounterId}/suggested-actions/${action.id}/execute`, { action: act });
      fetchEncounter();
      const labels = { send_to_nurse: 'Sent to nurse', done_by_provider: 'Confirmed', dismiss: 'Dismissed' };
      toast.success(`${action.action_name} — ${labels[act] || act}`);
    } catch {
      toast.error('Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`rounded-md border px-2.5 py-2 ${!isPending ? 'opacity-60' : ''}`}
      data-testid={`mini-lab-${index}`}>
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-medium flex-1 min-w-0 truncate">{action.action_name}</span>
        {action.confidence_label && (
          <Badge variant="outline" className={`text-[8px] h-3.5 px-1 ${
            action.confidence_label === 'likely' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
          }`}>
            {action.confidence_label}
          </Badge>
        )}
      </div>
      {action.summary && (
        <p className="text-[9px] text-muted-foreground mt-0.5 truncate">{action.summary}</p>
      )}

      {isPending ? (
        <div className="flex gap-1 mt-1.5">
          <Button size="sm" className="h-5 text-[9px] px-2 gap-0.5 flex-1 bg-emerald-600 hover:bg-emerald-700"
            disabled={loading}
            onClick={() => handleAction('done_by_provider')}
            data-testid={`mini-lab-confirm-${index}`}>
            {loading ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Check className="w-2.5 h-2.5" />}
            Confirm
          </Button>
          <Button size="sm" variant="outline" className="h-5 text-[9px] px-2 gap-0.5"
            disabled={loading}
            onClick={() => handleAction('send_to_nurse')}
            data-testid={`mini-lab-send-${index}`}>
            Send to Nurse
          </Button>
          <Button size="sm" variant="ghost" className="h-5 text-[9px] px-1 text-muted-foreground"
            disabled={loading}
            onClick={() => handleAction('dismiss')}
            data-testid={`mini-lab-dismiss-${index}`}>
            <X className="w-2.5 h-2.5" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-1 mt-1 text-[9px]">
          {action.status === 'done_by_provider' ? (
            <><Check className="w-2.5 h-2.5 text-emerald-500" /><span className="text-emerald-600">Confirmed</span></>
          ) : action.status === 'sent_to_nurse' ? (
            <><Check className="w-2.5 h-2.5 text-blue-500" /><span className="text-blue-600">Sent to nurse</span></>
          ) : (
            <><X className="w-2.5 h-2.5 text-slate-400" /><span className="text-slate-500">Dismissed</span></>
          )}
        </div>
      )}
    </div>
  );
}


function MiniPanelShell({ children, onClose, onOpenFull, title, count }) {
  return (
    <div className="mt-1.5 mb-1 rounded-lg border border-primary/20 bg-card shadow-sm animate-in fade-in-0 slide-in-from-top-1 duration-150"
      data-testid={`mini-panel-${title?.toLowerCase()}`}>
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50">
        <span className="text-[10px] font-semibold text-foreground flex items-center gap-1.5">
          {title}
          {count > 0 && <Badge variant="outline" className="text-[8px] h-3.5 px-1">{count}</Badge>}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={onOpenFull} className="text-[9px] text-primary hover:underline flex items-center gap-0.5"
            data-testid="mini-panel-open-full-labs">
            Full view <ArrowRight className="w-2.5 h-2.5" />
          </button>
          <button onClick={onClose} className="p-0.5 rounded hover:bg-accent" data-testid="mini-panel-close-labs">
            <X className="w-3 h-3 text-muted-foreground" />
          </button>
        </div>
      </div>
      <div className="px-3 py-2 max-h-64 overflow-y-auto">
        {children}
      </div>
    </div>
  );
}
